/**
 * Sort orders
 */
export const orderListConst = [
    {
        value: "asc",
        label: "A-Z",
    },
    {
        value: "desc",
        label: "Z-A",
    },
];

export const pageSizeConst = [
    {
        value: "9",
        label: "9",
    },
    {
        value: "27",
        label: "27",
    },
    {
        value: "48",
        label: "48",
    }
];

/**
 * Application type
 */
export const typeConst = {
    allDevices: "allDevices",
    website: "website",
    appleStore: "appStore",
    playStore: "playStore",
};

/**
 * Query parameters name
 */
export const queryParamName = {
    keyword: "q",
    categories: "cg[]",
    countries: "ct[]",
    languages: "ln[]",
    crops: "cp[]",
    type: "t",
    order: "o",
    page: "p",
    pageSize: "ps",
    includefilter: "i",
};

/**
 * Sort order names
 */
export const sortOrder = {
    acending: "asc",
    descending: "desc",
};

/**
 * Helper methods
 */
export const helpers = {
    /**
     * Sort list in ascending order based on a property
     * @param {string} [property]
     */
    sortOnAsc(property) {
        return function (a, b) {
            var val = (a[property] ?? "")
                .trim()
                .localeCompare((b[property] ?? "").trim());
            if (val >= 0) {
                if (val === 0) {
                    return 0;
                } else {
                    return 1;
                }
            } else {
                return -1;
            }
        };
    },

    /**
     * Sort list in descending order based on a property
     * @param {string} [property]
     */
    sortOnDesc(property) {
        return function (a, b) {
            var val = (a[property] ?? "")
                .trim()
                .localeCompare((b[property] ?? "").trim());
            if (val >= 0) {
                if (val === 0) {
                    return 0;
                } else {
                    return -1;
                }
            } else {
                return 1;
            }
        };
    },

    /**
     * Check whether the value is empty, null or undefined
     * @param {*} value
     * @returns true if empty, null or undefined else false
     */
    notEmptyNull: (value) =>
        value !== null && value !== "" && value !== undefined,

    /**
     *
     * @param {*} history
     * @param {*} appState
     */
    updateHistory: (history, appState) => {
        const params = new URLSearchParams();

        if (appState.selectedCategory.length > 0)
            appState.selectedCategory.forEach((s) => {
                params.append(queryParamName.categories, s.value);
            });

        if (appState.selectedCountry.length > 0)
            appState.selectedCountry.forEach((s) => {
                params.append(queryParamName.countries, s.value);
            });

        if (appState.selectedLanguage.length > 0)
            appState.selectedLanguage.forEach((s) => {
                params.append(queryParamName.languages, s.value);
            });

        if (appState.selectedCrop.length > 0)
            appState.selectedCrop.forEach((s) => {
                params.append(queryParamName.crops, s.value);
            });

        if (appState.type) params.append(queryParamName.type, appState.type);

        if (appState.keyword)
            params.append(queryParamName.keyword, appState.keyword);

        if (appState.order && appState.order.value !== orderListConst[0].value)
            params.append(queryParamName.order, appState.order.value);

        if (appState.pageSize && appState.pageSize.value !== pageSizeConst[0].value)
            params.append(queryParamName.pageSize, appState.pageSize.value);

        if (appState.page && appState.page > 1)
            params.append(queryParamName.page, appState.page);

        history.push({ search: params.toString() });
    },

    /**
     * Get application type name from type value
     * @param {String} value 
     * @returns typename
     */
    getTypeName: (value) => {
        let name = "";
        switch (value) {
            case typeConst.allDevices:
                name = "All devices";
                break;
            case typeConst.appleStore:
                name = "Apple Store";
                break;
            case typeConst.playStore:
                name = "Google Play";
                break;
            case typeConst.website:
                name = "Website";
                break;
            default:
                name = value;
                break;
        }

        return name;
    },

    /**
     * Create new state from old state, filters, API response
     * @param {Object} appState 
     * @param {object} filters 
     * @param {object} data 
     * @param {Boolean} includeFilter 
     * @returns new application state
     */
    transformData: (appState, filters, data, includeFilter) => {
        let state = { ...appState };

        if (includeFilter) {
            state.categories = data.filters.categories
                .map((s) => {
                    return { label: s.name, value: s.name };
                })
                .sort(helpers.sortOnAsc("label"));


            var countryGlobal = data.filters.countries.find(function (item) {
                return item.iso2Code.toLowerCase() === 'gbl';
            });

            var withoutGlobal = data.filters.countries.filter(function (item) {
                return item.iso2Code.toLowerCase() !== 'gbl';
            });

            state.countries = withoutGlobal
                .map((s) => {
                    return { label: s.name, value: s.iso2Code };
                })
                .sort(helpers.sortOnAsc("label"));

            if (countryGlobal) {
                state.countries.unshift({ label: countryGlobal.name, value: countryGlobal.iso2Code });
            }

            state.languages = data.filters.languages
                .map((s) => {
                    return { label: s.name, value: s.isoCode };
                })
                .sort(helpers.sortOnAsc("label"));

            let cropList = [];
            data.filters.crops.forEach((s) => {
                cropList.push({ label: s.pn, value: s.pn });
                if (s.an.length > 0) {
                    s.an.forEach((alternative) => {
                        cropList.push({ label: alternative, value: alternative });
                    });
                }
            });

            state.crops = cropList.sort(helpers.sortOnAsc("label"));
        }

        let order = appState.orderList.filter((s) => {
            return s.value === filters.order;
        });

        if (!(Array.isArray(order) && order.length === 1)) {
            order = appState.orderList[0];
        } else {
            order = order[0];
        }

        let pageSize = appState.pageSizeList.filter((s) => {
            return s.value === filters.pageSize;
        });

        if (!(Array.isArray(pageSize) && pageSize.length === 1)) {
            pageSize = appState.pageSizeList[0];
        } else {
            pageSize = pageSize[0];
        }

        state.selectedCategory = filters.categories
            .map((s) => {
                let category = state.categories.filter((x) => {
                    return x.value === s;
                });
                if (Array.isArray(category) && category.length === 1) {
                    return category[0];
                }
                return null;
            })
            .filter((s) => s !== null);

        state.selectedCountry = filters.countries
            .map((s) => {
                let country = state.countries.filter((x) => {
                    return x.value === s;
                });
                if (Array.isArray(country) && country.length === 1) {
                    return country[0];
                }
                return null;
            })
            .filter((s) => s !== null);

        state.selectedLanguage = filters.languages
            .map((s) => {
                let language = state.languages.filter((x) => {
                    return x.value === s;
                });
                if (Array.isArray(language) && language.length === 1) {
                    return language[0];
                }
                return null;
            })
            .filter((s) => s !== null);

        state.selectedCrop = filters.crops
            .map((s) => {
                let crop = state.crops.filter((x) => {
                    return x.value === s;
                });
                if (Array.isArray(crop) && crop.length === 1) return crop[0];
                else return null;
            })
            .filter((s) => s !== null);

        state.keyword = filters.keyword ?? "";
        state.type = filters.type;
        state.page = filters.page;
        state.order = order;
        state.pageSize = pageSize;

        state.results = data.results;
        state.resultCount = data.total;
        state.typecount = [
            {
                name: typeConst.allDevices,
                value: data.filters.searchResultCountByTypes.allDevices,
            },
            {
                name: typeConst.website,
                value: data.filters.searchResultCountByTypes.website,
            },
            {
                name: typeConst.appleStore,
                value: data.filters.searchResultCountByTypes.appleStore,
            },
            {
                name: typeConst.playStore,
                value: data.filters.searchResultCountByTypes.playStore,
            },
        ];

        return state;
    },

    /**
     * Get filter object from query params
     * @param {*} queryString 
     * @returns filters
     */
    getFilters: (queryString) => {
        const trustValue = (value) => {
            return value === "" || value === null ? null : value;
        };

        let value = {};

        const query = new URLSearchParams(queryString);

        value.keyword = trustValue(query.get(queryParamName.keyword));
        value.categories = query
            .getAll(queryParamName.categories)
            .filter((s) => helpers.notEmptyNull(s));
        value.countries = query
            .getAll(queryParamName.countries)
            .filter((s) => helpers.notEmptyNull(s));
        value.languages = query
            .getAll(queryParamName.languages)
            .filter((s) => helpers.notEmptyNull(s));
        value.crops = query
            .getAll(queryParamName.crops)
            .filter((s) => helpers.notEmptyNull(s));
        value.type = trustValue(query.get(queryParamName.type)) ?? "";
        value.order =
            trustValue(query.get(queryParamName.order)) ?? sortOrder.acending;
        value.page = trustValue(query.get(queryParamName.page)) ?? 1;
        value.pageSize =
            trustValue(query.get(queryParamName.pageSize)) ?? 9;

        return value;
    },
};
